import { ref, inject } from '@vue/composition-api'
import { format } from 'date-fns'
import * as Sentry from '@sentry/vue'
import { PrinterConnection } from '@yonarezi/yonarezi3.epos-printer'
import { generateCommand } from '@yonarezi/yonarezi3.slip-print/dist/utils/generate-epos-command'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import useCurrentData from '@/views/composable/useCurrentData'

// ASB (Auto Status Back) ビット定義（epos-2.27.0.js より）
// detected = ビットが立っている = 状態が「label」に該当する
// NOTE: BATTERY_OFFLINE はバッテリ運用前提のビット。AC給電のみの TM-m30III では
// AC接続中でも常時立っている可能性があるため、誤検知ノイズになるので除外。
const ASB_BITS = [
  {
    key: 'COVER_OPEN',
    value: 32,
    label: 'プリンタのカバー',
    okText: '閉まっています',
    ngText: '開いています（カバーを閉じてください）',
    isError: true,
  },
  {
    key: 'RECEIPT_END',
    value: 524288,
    label: 'ロール紙',
    okText: 'セットされています',
    ngText: '切れています（ロール紙を交換してください）',
    isError: true,
  },
  {
    key: 'RECEIPT_NEAR_END',
    value: 131072,
    label: 'ロール紙の残量警告',
    okText: 'なし（十分あります）',
    ngText: 'あり（予備のロール紙を準備してください）',
    isError: false,
  },
  {
    key: 'OFF_LINE',
    value: 8,
    label: 'プリンタの通信',
    okText: 'オンライン',
    ngText: 'オフライン（プリンタの電源とWiFi接続を確認してください）',
    isError: true,
  },
  {
    key: 'NO_RESPONSE',
    value: 1,
    label: 'プリンタからの応答',
    okText: 'あります',
    ngText: 'ありません（プリンタの電源を入れ直してください）',
    isError: true,
  },
  {
    key: 'AUTOCUTTER_ERR',
    value: 2048,
    label: '用紙カッター',
    okText: '正常',
    ngText: '異常（カッター部の紙詰まりを取り除き、プリンタを再起動してください）',
    isError: true,
  },
  {
    key: 'MECHANICAL_ERR',
    value: 1024,
    label: '本体の機械部分',
    okText: '正常',
    ngText: '異常（プリンタを再起動してください。改善しない場合はサポートにご連絡ください）',
    isError: true,
  },
  {
    key: 'UNRECOVER_ERR',
    value: 8192,
    label: '致命的エラー',
    okText: 'なし',
    ngText: 'あり（プリンタを再起動してください。改善しない場合は修理が必要です）',
    isError: true,
  },
  {
    key: 'SPOOLER_IS_STOPPED',
    value: 2147483648,
    label: '印刷キュー',
    okText: '稼働中',
    ngText: '停止中（プリンタを再起動してください）',
    isError: true,
  },
]

// グローバル open 状態（どこからでも診断ダイアログを開けるように）
const isOpen = ref(false)

export function openPrinterDiagnostic() {
  isOpen.value = true
}

export function closePrinterDiagnostic() {
  isOpen.value = false
}

const formatStatusHex = status => {
  if (typeof status !== 'number') return '—'
  // ASB_SPOOLER_IS_STOPPED など32bit目を含むため、unsigned で表示
  // eslint-disable-next-line no-bitwise
  const unsigned = status >>> 0

  return `0x${unsigned.toString(16).toUpperCase().padStart(8, '0')}`
}

export default function usePrinterDiagnostic() {
  const { currentClub } = useCurrentData()

  // Layout が provide している receiptPrinter（QR・レシート印刷と同じ接続を共有）
  // 同じ物理プリンタに対して別経路で接続を張ると ePOS SDK の応答ルーティングが
  // 混乱するため、必ず既存の接続を再利用する。
  const sharedReceiptPrinter = inject('printer', ref(null))

  const isRunning = ref(false)
  const connectionResult = ref(null) // { ok, error, ipAddress, port, source }
  const printResult = ref(null) // { ok, status, code, checks }
  const statusOnlyResult = ref(null) // { ok, status, checks, source }
  const lastRunAt = ref(null)
  const activePrinterRef = ref(null) // 直近に診断で使ったプリンタインスタンス

  const resolveIpAddress = () => removePortFromIpAddress(currentClub.value?.printIpAddress || '')

  const parseStatus = status => {
    if (typeof status !== 'number') return []

    return ASB_BITS.map(def => {
      // eslint-disable-next-line no-bitwise
      const detected = (status & def.value) !== 0

      return {
        key: def.key,
        label: def.label,
        text: detected ? def.ngText : def.okText,
        ok: !detected,
        isError: def.isError,
      }
    })
  }

  // プリンタの Web 設定 (port 443) への単純な HTTPS GET で到達可否を確認する。
  // ePOS デーモン (port 8043) とは別プロセスなので、これに到達できれば
  // 「プリンタ本体は生きている」と判定できる。
  //
  //   443 OK + ePOS NG → プリンタは生きているが ePOS がハング (電源 OFF/ON)
  //   443 NG + ePOS NG → プリンタ到達不可 (WiFi/電源/LAN 確認)
  const probePrinterReachable = async ipAddress => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 3000)
    try {
      await fetch(`https://${ipAddress}/`, {
        method: 'GET',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-store',
      })

      return true
    } catch {
      return false
    } finally {
      clearTimeout(timer)
    }
  }

  const runConnectionTest = async () => {
    const ipAddress = resolveIpAddress()
    if (!ipAddress) {
      connectionResult.value = {
        ok: false, error: '店舗のプリンタIPが設定されていません', ipAddress: '', port: null, source: null, httpReachable: null, offline: false,
      }

      return null
    }

    // インターネット切断を先に検出 (ERROR_TIMEOUT を15秒待たずに即判定できる)
    // 端末側 WiFi OFF / ネットワーク断などのケース
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      connectionResult.value = {
        ok: false,
        error: 'ネットワーク未接続 (net::ERR_INTERNET_DISCONNECTED)',
        ipAddress,
        port: null,
        source: null,
        printerReachable: false,
        offline: true,
      }

      return null
    }

    // Layout の receipt printer が既に接続済みならそれを使う（経路競合回避）
    const existing = sharedReceiptPrinter.value
    if (existing?.ePosDev?.isConnected()) {
      connectionResult.value = {
        ok: true,
        error: null,
        ipAddress,
        port: existing.ePosDev?.port ?? null,
        source: 'shared',
        printerReachable: true,
        offline: false,
      }
      activePrinterRef.value = existing

      return existing
    }

    // 未接続なら新規接続を試みる（既存経路と同じ PrinterConnection を使う）
    try {
      const printerConnection = new PrinterConnection({ ipAddress })
      const printer = await printerConnection.connect()
      connectionResult.value = {
        ok: true,
        error: null,
        ipAddress,
        port: printer.ePosDev?.port ?? null,
        source: 'new',
        printerReachable: true,
        offline: false,
      }
      activePrinterRef.value = printer

      return printer
    } catch (error) {
      // ePOS 接続が失敗 → Web設定 (port 443) の到達性で原因を切り分け
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      const printerReachable = offline ? false : await probePrinterReachable(ipAddress)
      connectionResult.value = {
        ok: false,
        error: offline
          ? 'ネットワーク未接続 (net::ERR_INTERNET_DISCONNECTED)'
          : (error?.message || String(error)),
        ipAddress,
        port: null,
        source: null,
        printerReachable,
        offline,
      }

      return null
    }
  }

  // 既存印刷と同じ receiptline → addCommand 方式でテスト印刷を送る
  const runTestPrint = printer => new Promise(resolve => {
    let resolved = false
    const finish = result => {
      if (resolved) return
      resolved = true
      resolve(result)
    }
    const timer = setTimeout(() => {
      finish({
        ok: false, status: undefined, code: 'TIMEOUT', checks: [],
      })
    }, 15000)

    // 直前に「ステータスのみ」で startMonitor を呼んでいた場合、
    // 印刷応答が onstatuschange 側に流れて onreceive が発火しない問題を回避するため、
    // 必ず monitor を停止し onstatuschange を外してからテスト印刷を送る。
    try { printer.stopMonitor() } catch (_) { /* noop */ }
    // eslint-disable-next-line no-param-reassign
    printer.onstatuschange = null

    // eslint-disable-next-line no-param-reassign
    printer.onreceive = response => {
      clearTimeout(timer)
      finish({
        ok: !!response?.success,
        status: response?.status,
        code: response?.code,
        checks: parseStatus(response?.status),
      })
    }

    const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss')
    const markdown = `
      {width: *}
      "^^診断テスト印刷^^"
      | - |
      {width: 10 *}
      |日時 |${timestamp}
      |店舗 |${currentClub.value?.name ?? ''}
    `.trim()

    try {
      printer.addCommand(generateCommand(markdown))
      printer.addCut(printer.CUT_FEED)
      printer.send()
    } catch (error) {
      clearTimeout(timer)
      finish({
        ok: false, status: undefined, code: error?.message || 'SEND_ERROR', checks: [],
      })
    }
  })

  const runDiagnostic = async () => {
    if (isRunning.value) return
    isRunning.value = true
    connectionResult.value = null
    printResult.value = null
    statusOnlyResult.value = null

    try {
      const printer = await runConnectionTest()
      if (printer) {
        printResult.value = await runTestPrint(printer)
      } else {
        // 接続失敗時は、テスト印刷・ステータス結果が意味を持たないので確実にクリア
        printResult.value = null
        statusOnlyResult.value = null
      }
      lastRunAt.value = new Date()
    } finally {
      isRunning.value = false
    }
  }

  // 紙を使わずに現在のステータスのみ取得する。
  // printer.startMonitor() + onstatuschange を一時的に仕掛けて値を1回拾う。
  const queryPrinterStatus = printer => new Promise(resolve => {
    let resolved = false
    const cleanup = () => {
      try { printer.stopMonitor() } catch (_) { /* noop */ }
      // eslint-disable-next-line no-param-reassign
      printer.onstatuschange = null
    }
    const finish = result => {
      if (resolved) return
      resolved = true
      cleanup()
      resolve(result)
    }

    const timer = setTimeout(() => {
      // タイムアウト時: printer.status プロパティに値があれば使う、なければ未取得
      const cached = typeof printer.status === 'number' ? printer.status : undefined
      finish({
        ok: typeof cached === 'number',
        status: cached,
        checks: parseStatus(cached),
        source: cached !== undefined ? 'cached' : 'timeout',
      })
    }, 5000)

    // eslint-disable-next-line no-param-reassign
    printer.onstatuschange = status => {
      clearTimeout(timer)
      finish({
        ok: true, status, checks: parseStatus(status), source: 'monitor',
      })
    }

    try {
      printer.startMonitor()
    } catch (_) {
      // startMonitor 失敗時は cached fallback を待つ（タイマーで拾う）
    }
  })

  const runStatusOnly = async () => {
    if (isRunning.value) return null
    isRunning.value = true
    statusOnlyResult.value = null
    // 「ステータスのみ」実行時は、前回のテスト印刷結果も消して紛らわしくないようにする
    printResult.value = null

    try {
      const printer = await runConnectionTest()
      if (!printer) {
        statusOnlyResult.value = null

        return null
      }

      const result = await queryPrinterStatus(printer)
      statusOnlyResult.value = result
      lastRunAt.value = new Date()

      return result
    } finally {
      isRunning.value = false
    }
  }

  const sendDiagnosticToSentry = () => {
    Sentry.captureMessage('Printer diagnostic submitted', {
      level: 'info',
      tags: { feature: 'printer', source: 'diagnostic', kind: 'receipt' },
      extra: {
        ipAddress: connectionResult.value?.ipAddress,
        port: connectionResult.value?.port,
        connectionSource: connectionResult.value?.source,
        clubId: currentClub.value?.id,
        clubName: currentClub.value?.name,
        connectionOk: connectionResult.value?.ok,
        connectionError: connectionResult.value?.error,
        // プリンタ本体への到達性 (Web設定 port 443 への HTTPS GET)
        // true → プリンタは生きている / false → プリンタに届かない
        printerReachable: connectionResult.value?.printerReachable,
        // 端末がオフライン状態だったか (navigator.onLine === false)
        clientOffline: connectionResult.value?.offline,
        printOk: printResult.value?.ok,
        printStatus: printResult.value?.status,
        printStatusHex: formatStatusHex(printResult.value?.status),
        printCode: printResult.value?.code,
        printChecks: printResult.value?.checks,
        statusOnlyStatus: statusOnlyResult.value?.status,
        statusOnlyStatusHex: formatStatusHex(statusOnlyResult.value?.status),
        statusOnlySource: statusOnlyResult.value?.source,
        lastRunAt: lastRunAt.value?.toISOString(),
      },
    })
  }

  return {
    isOpen,
    isRunning,
    connectionResult,
    printResult,
    statusOnlyResult,
    lastRunAt,
    runDiagnostic,
    runStatusOnly,
    sendDiagnosticToSentry,
    closePrinterDiagnostic,
    formatStatusHex,
  }
}
